import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Inbox as InboxIcon, Send, PenLine, Search, X, Trash2, Mail, MailOpen } from 'lucide-react';

export default function Inbox() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('inbox');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(false);

  useEffect(() => { if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);

  const load = () => {
    const path = tab === 'inbox' ? '/messages/inbox' : '/messages/sent';
    api.get(path).then(r => setItems(r.data)).catch(() => toast.error('Load failed'));
  };
  useEffect(() => { if (user) load(); }, [user, tab]);

  const open = async (m) => {
    setSelected(m);
    if (tab === 'inbox' && !m.read) {
      try { await api.patch(`/messages/${m.id}/read`); load(); }
      catch (err) { console.error('Failed to mark message read:', err); }
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await api.delete(`/messages/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
    toast.success('Deleted');
  };

  if (loading || !user) return <div className="py-24 text-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-10">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="overline mb-1">Direct Messages</div>
            <h1 className="font-serif text-4xl text-royal font-bold">Inbox</h1>
            <div className="text-sm text-charcoal-muted mt-1">Send and receive messages with any member across the jurisdiction.</div>
          </div>
          <button onClick={() => setComposing(true)} data-testid="compose-btn" className="btn-royal"><PenLine className="w-4 h-4" /> New Message</button>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => { setTab('inbox'); setSelected(null); }} data-testid="tab-inbox" className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 ${tab === 'inbox' ? 'bg-royal text-white' : 'bg-white border'}`}><InboxIcon className="w-4 h-4" /> Inbox</button>
          <button onClick={() => { setTab('sent'); setSelected(null); }} data-testid="tab-sent" className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 ${tab === 'sent' ? 'bg-royal text-white' : 'bg-white border'}`}><Send className="w-4 h-4" /> Sent</button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white border rounded-lg overflow-hidden md:col-span-1 max-h-[70vh] overflow-y-auto">
            {items.length === 0 && <div className="p-8 text-center text-sm text-charcoal-muted">No {tab === 'inbox' ? 'incoming' : 'sent'} messages.</div>}
            {items.map(m => (
              <button key={m.id} onClick={() => open(m)} data-testid={`msg-item-${m.id}`}
                className={`w-full text-left p-4 border-b hover:bg-gray-50 flex gap-2 ${selected?.id === m.id ? 'bg-royal/5' : ''} ${tab === 'inbox' && !m.read ? 'font-semibold' : ''}`}>
                {tab === 'inbox' ? (m.read ? <MailOpen className="w-4 h-4 text-gray-400 mt-1 shrink-0" /> : <Mail className="w-4 h-4 text-royal mt-1 shrink-0" />) : <Send className="w-4 h-4 text-gray-400 mt-1 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gold uppercase tracking-widest">{tab === 'inbox' ? m.from_name : `To: ${m.to_name}`}</div>
                  <div className="text-royal truncate">{m.subject}</div>
                  <div className="text-xs text-charcoal-muted mt-0.5">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="md:col-span-2">
            {selected ? (
              <div className="bg-white border rounded-lg p-6">
                <div className="flex justify-between items-start mb-4 gap-3">
                  <div>
                    <div className="overline mb-1">{tab === 'inbox' ? 'From' : 'To'}</div>
                    <div className="font-serif text-lg text-royal font-bold">{tab === 'inbox' ? selected.from_name : selected.to_name}</div>
                    <div className="text-xs text-charcoal-muted">{new Date(selected.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => remove(selected.id)} className="text-red-500 p-2 hover:bg-red-50 rounded" data-testid="msg-delete-btn"><Trash2 className="w-4 h-4" /></button>
                </div>
                <h3 className="font-serif text-2xl text-royal font-bold mb-4">{selected.subject}</h3>
                <div className="text-charcoal-muted whitespace-pre-wrap leading-relaxed">{selected.body}</div>
                {tab === 'inbox' && (
                  <button onClick={() => setComposing({ reply_to: selected.from_user_id, reply_to_name: selected.from_name, subject: `Re: ${selected.subject}` })}
                    className="mt-6 btn-royal text-sm">Reply</button>
                )}
              </div>
            ) : (
              <div className="bg-white border rounded-lg p-12 text-center text-charcoal-muted">Select a message to read</div>
            )}
          </div>
        </div>
      </div>

      {composing && <Compose initial={typeof composing === 'object' ? composing : null} onClose={() => setComposing(false)} onSent={() => { setComposing(false); load(); }} />}
    </div>
  );
}

function Compose({ initial, onClose, onSent }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [to, setTo] = useState(initial?.reply_to || '');
  const [toName, setToName] = useState(initial?.reply_to_name || '');
  const [subject, setSubject] = useState(initial?.subject || '');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/messages/users').then(r => setUsers(r.data)).catch(() => {}); }, []);
  const filtered = useMemo(() => users.filter(u =>
    u.name?.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase())
  ).slice(0, 8), [users, q]);

  const send = async () => {
    if (!to || !subject || !body) { toast.error('Recipient, subject, and body required'); return; }
    setBusy(true);
    try {
      await api.post('/messages', { to_user_id: to, subject, body });
      toast.success('Message sent');
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full my-8 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif text-xl text-royal font-bold">New Message</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-royal mb-1">To</div>
            {to && toName ? (
              <div className="flex items-center gap-2 p-2 bg-royal/5 rounded">
                <span className="text-royal font-semibold flex-1">{toName}</span>
                <button onClick={() => { setTo(''); setToName(''); }} className="text-xs text-red-500">Change</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-muted" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name or email" data-testid="recipient-search" className="w-full pl-10 pr-3 py-2 border rounded" />
                </div>
                {q && filtered.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded">
                    {filtered.map(u => (
                      <button key={u.user_id} onClick={() => { setTo(u.user_id); setToName(u.name); setQ(''); }} data-testid={`recipient-${u.user_id}`}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 border-b last:border-0">
                        <div className="w-8 h-8 rounded-full bg-royal/10 flex items-center justify-center text-royal font-serif text-sm">{u.name?.[0] || 'U'}</div>
                        <div className="flex-1"><div className="text-sm font-semibold text-royal">{u.name}</div><div className="text-xs text-charcoal-muted">{u.email}</div></div>
                        <div className="text-[10px] text-gold uppercase">{u.role}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <div className="text-sm font-semibold text-royal mb-1">Subject</div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="msg-subject" className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <div className="text-sm font-semibold text-royal mb-1">Message</div>
            <textarea rows="8" value={body} onChange={(e) => setBody(e.target.value)} data-testid="msg-body" className="w-full px-3 py-2 border rounded" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-charcoal-muted">Cancel</button>
          <button onClick={send} disabled={busy} data-testid="msg-send-btn" className="btn-royal">{busy ? 'Sending...' : 'Send'}</button>
        </div>
      </div>
    </div>
  );
}
